import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import React, { useCallback, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DocumentForListing } from "../../../services/models/DocumentForListing";
import apiClient from "../../../utils/api";
import { ThumbnailViewer } from "../../AdminComponents/ClassPage/ThumbnailViewer";
import { Material } from "../../../services/models/Material";
import { MaterialEditRequestMaterialTypeEnum } from "../../../services/models/MaterialEditRequest";

export const ProfessorInterface: React.FC = () => {
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [materialType, setMaterialType] =
    useState<MaterialEditRequestMaterialTypeEnum>("class materials");
  const [availableDate, setAvailableDate] = useState<Date>(new Date());
  const classID = useLocation().pathname.split("/")[2];
  const materialID = useLocation().pathname.split("/").pop() || "";
  const [documents, setDocuments] = React.useState<DocumentForListing[]>();
  const [materialInfo, setMaterialInfo] = React.useState<Material>();
  console.log("classID", classID);
  console.log("materialID", materialID);

  const navigate = useNavigate();

  const handleDocumentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Handle the file upload logic here
      console.log("file uploaded by the professor", file);
      try {
        const res = await apiClient.documentControllerUpload({
          file: file,
          classMaterialId: materialID,
        });
        if (res) {
          console.log(res);
          setDocumentUploaded(true);
          fetchDocumentList();
        }
      } catch (error) {
        console.error("Error uploading document", error);
      }
    }
  };

  const fetchDocumentList = useCallback(async () => {
    try {
      const { documents } =
        await apiClient.materialControllerGetListByMaterialId({
          materialID: materialID,
        });
      console.log("documents: ", documents);
      setDocuments(documents);
    } catch (error) {
      console.log("error fetching class material", error);
    }
  }, [materialID]);

  // Get details of the class material
  const fetchMaterialInfo = useCallback(
    async (materialID: string) => {
      try {
        const materials = await apiClient.materialControllerList({
          materialListRequest: { classId: classID },
        });
        const material = materials.materials.find(
          (material) => material.id === materialID
        );
        setMaterialInfo(material);
        if (material) {
          setAvailableDate(material.availableDate);
          setMaterialType(
            material.materialType as MaterialEditRequestMaterialTypeEnum
          );
        }
      } catch (error) {
        console.error("Error fetching class material", error);
      }
    },
    [classID]
  );

  React.useEffect(() => {
    fetchDocumentList();

    fetchMaterialInfo(materialID);
  }, [fetchDocumentList, fetchMaterialInfo, materialID]);

  const handleEditMaterial = async () => {
    try {
      const res = await apiClient.materialControllerEdit({
        materialEditRequest: {
          materialId: materialID,
          materialType: materialType,
          availableDate: availableDate,
        },
      });
      if (res) {
        console.log(res);
        setEditMode(false);
        fetchMaterialInfo(materialID);
      }
    } catch (error) {
      console.error("Error editing material", error);
    }
  };

  const handleDeleteMaterial = async () => {
    try {
      await apiClient.materialControllerDelete({
        materialDeleteRequest: {
          materialId: materialID,
        },
      });
      // Redirect to class material list page
      navigate(`/admin/${classID}`);
    } catch (error) {
      console.error("Error deleting material", error);
    }
  };

  return (
    <div>
      <h1>Professor Interface</h1>
      <h2>Looking at class material {materialInfo?.id}</h2>
      {editMode ? (
        <div>
          <FormControl>
            <InputLabel id="material-type-label">Material Type</InputLabel>
            <Select
              labelId="material-type-label"
              label="Material Type"
              value={materialType}
              onChange={(e) =>
                setMaterialType(
                  e.target.value as MaterialEditRequestMaterialTypeEnum
                )
              }
            >
              {Object.values(MaterialEditRequestMaterialTypeEnum).map(
                (value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <TextField
            label="Available Date"
            type="date"
            value={availableDate.toISOString().split("T")[0]}
            onChange={(e) => setAvailableDate(new Date(e.target.value))}
          />
          <Button variant="contained" onClick={handleEditMaterial}>
            Save Changes
          </Button>
        </div>
      ) : (
        <div>
          <Button variant="contained" onClick={() => setEditMode(true)}>
            Edit Material
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteMaterial}
          >
            Delete Material
          </Button>
        </div>
      )}
      <h2>Upload File for class of Type: {materialInfo?.materialType}</h2>
      <h3>
        This class material will be available after{" "}
        {materialInfo?.availableDate.toDateString()}
      </h3>
      <Button
        component="label"
        variant="contained"
        startIcon={<CloudUploadIcon />}
      >
        Upload file
        <input
          accept=".pdf, .doc, .docx. .ppt, .pptx .png, .jpg, .jpeg"
          type="file"
          style={{ display: "none" }}
          onChange={handleDocumentUpload}
        />
      </Button>
      <div className="documents">
        <h3>*View documents below*</h3>
        <Grid container columns={{ xs: 4, sm: 8, md: 12 }}>
          {documents &&
            documents.map((document, index) => {
              return (
                <Grid item xs={2} sm={4} md={4} key={index}>
                  <div style={{ textAlign: "center" }}>
                    <Link
                      to={`/admin/document/${classID}/${document.id}`}
                      target="_blank"
                    >
                      <ThumbnailViewer pdfFile={document.file} />
                      <h4> {document.fileName}</h4>
                      <Button component="label" variant="contained">
                        Annotate PDF
                      </Button>
                    </Link>
                  </div>
                </Grid>
              );
            })}
        </Grid>
      </div>
      {documentUploaded && (
        <Alert
          severity="success"
          onClose={() => setDocumentUploaded(false)}
          style={{ position: "fixed", top: "20px", right: "700px" }}
        >
          Document uploaded successfully!
        </Alert>
      )}
    </div>
  );
};
